import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import { CatalogSearchResultListItem } from '@backstage/plugin-catalog';
import {
  catalogApiRef,
  CATALOG_FILTER_EXISTS,
} from '@backstage/plugin-catalog-react';
import { TechDocsSearchResultListItem } from '@backstage/plugin-techdocs';
import { SearchType } from '@backstage/plugin-search';
import {
  SearchBar,
  SearchFilter,
  SearchPagination,
  SearchResultContext,
  useSearch,
} from '@backstage/plugin-search-react';
import {
  CatalogIcon,
  Content,
  DocsIcon,
  Header,
  Link,
  Page,
} from '@backstage/core-components';
import List from '@mui/material/List';
import { useApi } from '@backstage/core-plugin-api';
import {
  getSurfaceBorder,
  getSurfaceColor,
  getThemeTokens,
  radii,
  shellMetrics,
} from '../../theme/tokens';

const getDocumentOwner = (document: object) => {
  if (!('owner' in document)) {
    return undefined;
  }

  return typeof document.owner === 'string' && document.owner
    ? document.owner
    : undefined;
};

const SearchPage = () => {
  const theme = useTheme();
  const { types } = useSearch();
  const catalogApi = useApi(catalogApiRef);
  const tokens = getThemeTokens(theme);

  return (
    <Page themeId="home">
      <Header
        title="Search"
        subtitle="Find software, documentation, and platform knowledge across the portal."
      />
      <Content>
        <Box
          sx={{
            boxSizing: 'border-box',
            maxWidth: 'none',
            px: { xs: 2, md: 3 },
            width: '100%',
          }}
        >
          <Stack spacing={shellMetrics.sectionGap / 8}>
            <Paper
              sx={{
                background: `linear-gradient(135deg, ${getSurfaceColor(
                  theme,
                  'raised',
                )} 0%, ${getSurfaceColor(theme, 'subtle')} 100%)`,
                border: `1px solid ${getSurfaceBorder(theme)}`,
                borderRadius: `${radii.xl}px`,
                px: { xs: 2.5, md: 3 },
                py: { xs: 2, md: 2.25 },
              }}
            >
              <Box
                sx={{
                  '& .MuiPaper-root': {
                    backgroundColor: 'transparent',
                  },
                  '& .MuiInputBase-root': {
                    backgroundColor: getSurfaceColor(theme, 'raised'),
                    border: `1px solid ${getSurfaceBorder(theme)}`,
                    borderRadius: `${radii.md}px`,
                    minHeight: 48,
                    px: 1.5,
                  },
                  '& .MuiInputBase-input': {
                    fontSize: theme.typography.body1.fontSize,
                  },
                  '& .MuiButtonBase-root': {
                    borderRadius: `${radii.sm}px`,
                  },
                }}
              >
                <SearchBar />
              </Box>
            </Paper>

            <Box
              sx={{
                display: 'grid',
                gap: 2.5,
                gridTemplateColumns: {
                  xs: '1fr',
                  md: '312px minmax(0, 1fr)',
                },
              }}
            >
              <Paper
                sx={{
                  alignSelf: 'start',
                  backgroundColor: getSurfaceColor(theme, 'subtle'),
                  border: `1px solid ${getSurfaceBorder(theme)}`,
                  borderRadius: `${radii.xl}px`,
                  px: 2.75,
                  py: 2.75,
                  position: { md: 'sticky' },
                  top: { md: theme.spacing(3) },
                }}
              >
                <Stack spacing={2.5}>
                  <Box>
                    <Typography variant="subtitle2">Refine results</Typography>
                    <Typography
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                      variant="body2"
                    >
                      Narrow by type, entity, and lifecycle.
                    </Typography>
                  </Box>

                  <Box>
                    <SearchType.Accordion
                      name="Result Type"
                      types={[
                        {
                          value: 'software-catalog',
                          name: 'Software Catalog',
                          icon: <CatalogIcon />,
                        },
                        {
                          value: 'techdocs',
                          name: 'Documentation',
                          icon: <DocsIcon />,
                        },
                      ]}
                    />
                  </Box>

                  <Stack spacing={2}>
                    {types.includes('techdocs') && (
                      <SearchFilter.Select
                        label="Entity"
                        name="name"
                        values={async () => {
                          const { items } = await catalogApi.getEntities({
                            fields: ['metadata.name'],
                            filter: {
                              'metadata.annotations.backstage.io/techdocs-ref':
                                CATALOG_FILTER_EXISTS,
                            },
                          });

                          const names = items.map(
                            entity => entity.metadata.name,
                          );
                          names.sort();
                          return names;
                        }}
                      />
                    )}
                    <SearchFilter.Select
                      label="Kind"
                      name="kind"
                      values={['Component', 'Template']}
                    />
                    <SearchFilter.Checkbox
                      label="Lifecycle"
                      name="lifecycle"
                      values={['experimental', 'production']}
                    />
                  </Stack>
                </Stack>
              </Paper>

              <Paper
                sx={{
                  backgroundColor: getSurfaceColor(theme, 'raised'),
                  border: `1px solid ${getSurfaceBorder(theme)}`,
                  borderRadius: `${radii.xl}px`,
                  minHeight: 520,
                  overflow: 'hidden',
                }}
              >
                <SearchResultContext>
                  {({ loading, error, value }) => (
                    <Stack spacing={0}>
                      <Box
                        sx={{
                          borderBottom: `1px solid ${getSurfaceBorder(theme)}`,
                          px: { xs: 2, md: 2.5 },
                          py: 1.5,
                        }}
                      >
                        <Stack
                          alignItems="center"
                          direction="row"
                          justifyContent="space-between"
                        >
                          <Stack
                            alignItems="baseline"
                            direction="row"
                            spacing={1}
                          >
                            <Typography variant="subtitle1">Results</Typography>
                            {value?.results && (
                              <Typography
                                color="text.secondary"
                                variant="caption"
                              >
                                {value.numberOfResults != null
                                  ? `${value.numberOfResults} found`
                                  : value.nextPageCursor
                                    ? `${value.results.length}+ found`
                                    : `${value.results.length} found`}
                              </Typography>
                            )}
                          </Stack>
                          <Box
                            sx={{
                              color: tokens.textSecondary,
                              '& .MuiTablePagination-root': {
                                color: 'inherit',
                                m: 0,
                              },
                              '& .MuiToolbar-root': {
                                minHeight: 'unset',
                                p: 0,
                              },
                            }}
                          >
                            <SearchPagination />
                          </Box>
                        </Stack>
                      </Box>

                      <Box
                        sx={{
                          px: { xs: 2, md: 2.5 },
                          py: { xs: 1.5, md: 2 },
                          '& .MuiListItem-root': {
                            borderBottom: `1px solid ${getSurfaceBorder(
                              theme,
                            )}`,
                            py: 1.75,
                          },
                          '& .MuiListItem-root:last-child': {
                            borderBottom: 'none',
                          },
                          '& .MuiListItem-root:hover': {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.03,
                            ),
                          },
                          '& .search-owner-label': {
                            color: tokens.textMuted,
                            float: 'right',
                            fontSize: theme.typography.caption.fontSize,
                            lineHeight: '24px',
                            ml: 2,
                            textDecoration: 'none',
                            '&:hover': {
                              color: theme.palette.primary.main,
                            },
                          },
                        }}
                      >
                        {!loading && !error && value && (
                          <List disablePadding>
                            {value.results.map(
                              ({ document, highlight, type }, index) => {
                                const owner = getDocumentOwner(document);

                                return (
                                  <Box key={`${document.location}-${index}`}>
                                    {owner && (
                                      <Link
                                        className="search-owner-label"
                                        to={`/catalog/default/group/${owner}`}
                                      >
                                        {owner}
                                      </Link>
                                    )}
                                    {type === 'techdocs' ? (
                                      <TechDocsSearchResultListItem
                                        icon={<DocsIcon />}
                                        result={document}
                                        highlight={highlight}
                                        lineClamp={2}
                                      />
                                    ) : (
                                      <CatalogSearchResultListItem
                                        icon={<CatalogIcon />}
                                        result={document}
                                        highlight={highlight}
                                        lineClamp={2}
                                      />
                                    )}
                                  </Box>
                                );
                              },
                            )}
                          </List>
                        )}
                      </Box>
                    </Stack>
                  )}
                </SearchResultContext>
              </Paper>
            </Box>
          </Stack>
        </Box>
      </Content>
    </Page>
  );
};

export const searchPage = <SearchPage />;
